import { addOutliers, removeOutlier, setRegressionModel } from "@/api";
import { OutlierManagement } from "@/components/adjustments/OutlierManagement";
import { TrendMethodSelector } from "@/components/adjustments/TrendMethodSelector";
import AdjustmentSteps from "@/components/AdjustmentSteps";
import AuthenticatedLayout from "@/components/AuthenticatedLayout";
import BackToDashboard from "@/components/BackToDashboard";
import { PropertyHeader } from "@/components/PropertyHeader";
import ResponsiveContainer from "@/components/ResponsiveContainer";
import { Scatterplot } from "@/components/Scatterplot";
import StepTitleSection from "@/components/StepTitleSection";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import useFetch from "@/hooks/useFetch";
import { normalizeDataBasedOnVersion } from "@/lib/PreparedDataset";
import { getStandardDeviation, regression } from "@/lib/stats";
import { cn } from "@/lib/utils";
import { PageProps } from "@/types/PageProps";
import RegressionModel from "@/types/RegressionModel";
import { Adjustment } from "@/types/serializers";
import { Head, router, useForm } from "@inertiajs/react";
import { ArrowRightIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import { GrHelpBook } from "react-icons/gr";

type Comp = { id: string };
type Props = PageProps & {
  adjustment: Adjustment;
  dataset_url: string | null;
  regression_model: RegressionModel | null;
  comps: Comp[];
  outliers: string[];
};

export default function Component({
  user,
  adjustment,
  errors,
  notice,
  dataset_url,
  regression_model,
  outliers,
}: Props) {
  const { data, isPending, error } = useFetch(dataset_url);
  const { post: proceedToNext, processing: proceedProcessing } = useForm();

  const scatterplotData = useMemo(
    () => (data ? normalizeDataBasedOnVersion(data, outliers) : []),
    [data, outliers],
  );

  const onAutoExcludeClick = useCallback(async () => {
    if (!regression_model) return;

    const stdDev = getStandardDeviation(scatterplotData.map((p) => p.price));
    if (!stdDev) return;

    const regressionResult = regression(
      regression_model,
      scatterplotData.filter((i) => !i.outlier).map((i) => ({ x: i.date.getTime(), y: i.price })),
    );
    if (!regressionResult) return;

    const outliers = scatterplotData.filter((p) => {
      const price = regressionResult.predict(p.date.getTime());
      if (price === null) return false;
      if (p.price > price + stdDev * 2) {
        return true;
      }
      if (p.price < price - stdDev * 2) {
        return true;
      }
      return false;
    });

    await addOutliers(
      adjustment.public_id,
      outliers.map((o) => o.id),
    );
    router.reload({ only: ["outliers"] });
  }, [regression_model, scatterplotData, adjustment.public_id]);

  const outlierProperties = useMemo(
    () => scatterplotData?.filter((p) => outliers.includes(p.id)) || [],
    [scatterplotData, outliers],
  );

  const handleModelChange = useCallback(
    async (model: RegressionModel) => {
      await setRegressionModel(adjustment.public_id, model);
      router.reload({ only: ["regression_model"] });
    },
    [adjustment.public_id],
  );

  const handleRemoveOutlier = useCallback(
    async (id: string) => {
      await removeOutlier(adjustment.public_id, id);
      router.reload({ only: ["outliers"] });
    },
    [adjustment.public_id],
  );

  const handlePointClick = useCallback(
    async (point: { id: string; outlier?: boolean }) => {
      if (point.outlier) {
        await removeOutlier(adjustment.public_id, point.id);
      } else {
        await addOutliers(adjustment.public_id, [point.id]);
      }
      router.reload({ only: ["outliers"] });
    },
    [adjustment.public_id],
  );

  return (
    <AuthenticatedLayout user={user} errors={errors} notice={notice}>
      <Head title={`Market Trend - ${adjustment.address}`} />
      <div className="py-4">
        <BackToDashboard />

        <AdjustmentSteps currentStep="trend" adjustment={adjustment} className="mb-4" />
        <PropertyHeader adjustment={adjustment} />

        <div className="flex pt-4 space-x-8 mx-auto">
          <div className="flex-grow max-w-5xl">
            <div className="w-full">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">
                    <div>Find the trend line</div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isPending && <div>Loading...</div>}
                  {error && <div>Error: {error}</div>}
                  {scatterplotData && (
                    <ResponsiveContainer aspectRatio={16 / 9} maxHeight={600}>
                      <Scatterplot
                        width={700}
                        height={500}
                        data={scatterplotData}
                        regressionType={regression_model}
                        onPointClick={handlePointClick}
                      />
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
          <div className="max-w-md flex-grow">
            <div className="w-full space-y-4">
              <div className="space-y-6">
                <div className="space-y-4">
                  <StepTitleSection
                    title="Market Trend"
                    description={
                      <div className="space-y-4">
                        <div>Select the data for your analysis.</div>
                        <div>
                          <Alert variant="muted">
                            <GrHelpBook className="w-5 h-5" />
                            <AlertTitle>Need some help?</AlertTitle>
                            <AlertDescription>
                              <a
                                href="https://teeba-calculator.com/instructions#market-trend"
                                className="text-primary font-bold hover:underline"
                                target="_blank"
                              >
                                Click here
                              </a>{" "}
                              to view more detailed instructions.
                            </AlertDescription>
                          </Alert>
                        </div>
                      </div>
                    }
                  >
                    {regression_model && (
                      <Button
                        className={cn(buttonVariants(), "w-full")}
                        onClick={() => {
                          proceedToNext(`/adjustments/${adjustment.public_id}/trend`, {
                            preserveScroll: true,
                            preserveState: true,
                          });
                        }}
                        disabled={proceedProcessing}
                      >
                        {proceedProcessing ? "Processing..." : "Next"}{" "}
                        <ArrowRightIcon className="w-4 h-4" />
                      </Button>
                    )}
                  </StepTitleSection>

                  <TrendMethodSelector
                    regressionModel={regression_model}
                    onModelChange={handleModelChange}
                  />

                  {regression_model && (
                    <OutlierManagement
                      outlierProperties={outlierProperties}
                      onAutoExcludeClick={onAutoExcludeClick}
                      onRemoveOutlier={handleRemoveOutlier}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
